import React, { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';

import LuxLoyaltyAccountChip from '~/components/LuxLoyalty/LuxLoyaltyAccountChip';

import { InfiniteApplication, InfiniteApplicationApplicant } from '~/services/LuxLoyaltyInfiniteApplicationService';
import UsersService from '~/services/UsersService';

import { getLuxLoyaltyTierDisplayName } from '~/utils/luxLoyalty';

import { EnrolmentPath, normalisePhonePrefix } from '../InfiniteConcierge/createInfiniteAccountShared';
import { useInfiniteCustomerLoyaltyLookup } from '../InfiniteConcierge/useInfiniteCustomerLoyaltyLookup';

import { getTitleLabel } from './infiniteApplicationReview.utils';

export type ApplicantAccountState = {
  isChecking: boolean;
  isInfinite: boolean;
  customerId: string | null;
  infiniteProfile: App.LuxLoyaltyInfiniteProfile | null;
};

export type InfiniteApplicationEnrolmentRequest = {
  application: InfiniteApplication;
  applicant: InfiniteApplicationApplicant;
  path: EnrolmentPath;
};

type UserSearchSummary = {
  id_member: string;
  email?: string;
};

type UserAccountDetails = App.User & {
  title?: string | null;
};

function ExistingProfileIndicator() {
  return (
    <Chip color="error" label="User has existing Infinite profile" size="small" sx={{ alignSelf: 'flex-start' }} />
  );
}

function normalizeComparableText(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function normalizePhone(value: string | number | null | undefined): string {
  return String(value ?? '').replace(/[^0-9]/g, '');
}

function getApplicantAccountMismatchFields(
  applicant: InfiniteApplicationApplicant,
  user: UserAccountDetails | undefined,
): Array<string> {
  if (!user) {
    return [];
  }

  const mismatches: Array<string> = [];
  const applicantTitle = normalizeComparableText(getTitleLabel(applicant.title));
  const accountTitle = normalizeComparableText(user.title);
  const applicantName = normalizeComparableText(`${applicant.firstName} ${applicant.lastName}`);
  const accountName = normalizeComparableText(`${user.givenName ?? ''} ${user.surname ?? ''}`);
  const applicantPhone = `${normalisePhonePrefix(applicant.phonePrefix)} ${normalizePhone(applicant.phone)}`;
  const accountPhone = `${normalisePhonePrefix(user.phone_prefix)} ${normalizePhone(user.phone)}`;

  if (applicantTitle !== accountTitle) {
    mismatches.push('Title');
  }

  if (applicantName !== accountName) {
    mismatches.push('Name');
  }

  if (applicantPhone !== accountPhone) {
    mismatches.push('Phone');
  }

  return mismatches;
}

export function ApprovedApplicationEnrolmentAction({
  application,
  applicant,
  onStartEnrolment,
  onAccountStateChange,
}: {
  application: InfiniteApplication;
  applicant: InfiniteApplicationApplicant;
  onStartEnrolment: (request: InfiniteApplicationEnrolmentRequest) => void;
  onAccountStateChange: (applicantId: string, state: ApplicantAccountState) => void;
}) {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const email = applicant.email.trim();
  const usersQuery = useQuery({
    queryKey: ['users', 'infinite-application-email', email, brand],
    queryFn: ({ signal }) =>
      UsersService.getUsers(
        {
          filter: email,
          limit: 10,
          offset: 0,
          brand,
        },
        { signal },
      ) as Promise<Array<UserSearchSummary>>,
    enabled: !!email,
  });
  const matchingUser = usersQuery.data?.find((user) => user.email?.toLowerCase() === email.toLowerCase());
  const { accountSummary, currentTier, hasSocieteAccount, accountLookupFailed, accountLookupPending } =
    useInfiniteCustomerLoyaltyLookup(matchingUser?.id_member);
  const userDetailsQuery = useQuery({
    queryKey: ['users', 'infinite-application-account-details', matchingUser?.id_member, brand],
    queryFn: ({ signal }) =>
      UsersService.getUser(matchingUser?.id_member, { brand, signal }) as Promise<UserAccountDetails>,
    enabled: Boolean(matchingUser?.id_member && currentTier === 'infinite'),
  });
  const mismatchFields = getApplicantAccountMismatchFields(applicant, userDetailsQuery.data);

  useEffect(() => {
    onAccountStateChange(applicant.id, {
      isChecking: usersQuery.isLoading || accountLookupPending,
      isInfinite: currentTier === 'infinite' && Boolean(matchingUser?.id_member),
      customerId: currentTier === 'infinite' ? (matchingUser?.id_member ?? null) : null,
      infiniteProfile: accountSummary?.infiniteProfile ?? null,
    });
  }, [
    accountLookupPending,
    accountSummary?.infiniteProfile,
    applicant.id,
    currentTier,
    matchingUser?.id_member,
    onAccountStateChange,
    usersQuery.isLoading,
  ]);

  if (usersQuery.isLoading || accountLookupPending) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Checking account
        </Typography>
      </Stack>
    );
  }

  if (usersQuery.error instanceof Error) {
    return (
      <Typography variant="caption" color="warning.main">
        Could not check account
      </Typography>
    );
  }

  if (!matchingUser) {
    return (
      <Stack spacing={0.75} alignItems="flex-start">
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          No account found
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onStartEnrolment({ application, applicant, path: 'new' })}
        >
          Create new Infinite account
        </Button>
        <Typography variant="caption" color="text.secondary">
          If you have just created the account, refresh to see it here.
        </Typography>
      </Stack>
    );
  }

  if (accountLookupFailed) {
    return (
      <Stack spacing={0.75} alignItems="flex-start">
        <Typography variant="caption" color="warning.main">
          Tier check failed
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onStartEnrolment({ application, applicant, path: 'existing' })}
        >
          Upgrade user to Infinite
        </Button>
      </Stack>
    );
  }

  if (currentTier === 'infinite') {
    const openUserProfile = () => {
      window.open(`/users/${matchingUser.id_member}`, '_blank', 'noopener,noreferrer');
    };

    return (
      <Stack spacing={0.75} alignItems="flex-start">
        {accountSummary?.infiniteProfile && <ExistingProfileIndicator />}
        {accountSummary && (
          <LuxLoyaltyAccountChip
            luxLoyaltyAccount={accountSummary as App.LuxLoyaltyAccount}
            size="small"
            onClick={openUserProfile}
          />
        )}
        {userDetailsQuery.isLoading && (
          <Typography variant="caption" color="text.secondary">
            Checking account details
          </Typography>
        )}
        {mismatchFields.length > 0 && (
          <>
            <Alert
              severity="warning"
              sx={{
                py: 0,
                px: 1,
                '& .MuiAlert-icon': { py: 0.5 },
                '& .MuiAlert-message': { py: 0.5 },
              }}
            >
              Below applicant details do not exactly match account details:
              <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                {mismatchFields.map((field) => (
                  <Box component="li" key={field}>
                    {field}
                  </Box>
                ))}
              </Box>
              You might want to:
            </Alert>
            <Button
              size="small"
              variant="outlined"
              component={Link}
              to={`/users/${matchingUser.id_member}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Update account details
            </Button>
          </>
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={0.75} alignItems="flex-start">
      <Typography variant="caption" color="warning.main" sx={{ fontWeight: 700 }}>
        {hasSocieteAccount && currentTier
          ? `${getLuxLoyaltyTierDisplayName(currentTier)} account found`
          : 'Non-Infinite account found'}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={() => onStartEnrolment({ application, applicant, path: 'existing' })}
      >
        Upgrade user to Infinite
      </Button>
      <Typography variant="caption" color="text.secondary">
        If you have just upgraded the account, refresh to see it here.
      </Typography>
    </Stack>
  );
}
